import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, CircularProgress, Alert, Grid, Card,
  CardContent, Select, MenuItem, FormControl, InputLabel, Stack, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip as ChartTooltip, Legend, ArcElement, Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import { useAuth } from './AuthContext';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Title, ChartTooltip, Legend, ArcElement, Filler,
);

const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';

const CHART_COLORS = [
  '#43a047', '#1976d2', '#f57c00', '#e53935', '#8e24aa',
  '#00acc1', '#ffb300', '#5c6bc0', '#d81b60', '#00897b',
];

const StatCard = ({ title, value, subtitle, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
      <Typography variant="h3" fontWeight={700} sx={{ color: color || 'primary.main' }}>{value}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </CardContent>
  </Card>
);

const chartOptions = (titleText, yLabel) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { boxWidth: 12, usePointStyle: true } },
    title: { display: true, text: titleText, font: { size: 14, weight: 600 } },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, title: { display: !!yLabel, text: yLabel }, ticks: { precision: 0 } },
  },
});

export default function AdminAnalytics() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const isMobile = useMediaQuery('(max-width:600px)');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${API_URL}/admin/analytics?days=${days}`, { headers });
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Access denied. Superuser privileges required.' : 'Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress color="primary" size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" variant="filled">{error}</Alert>
      </Container>
    );
  }

  if (!data) return null;

  const sortedDays = Object.keys(data.logins_by_day).sort();
  const allDays = Object.keys({ ...data.logins_by_day, ...data.events_by_day, ...data.daily_active_users }).sort();

  const loginsChartData = {
    labels: sortedDays,
    datasets: [{
      label: 'Logins',
      data: sortedDays.map(d => data.logins_by_day[d] || 0),
      borderColor: '#43a047',
      backgroundColor: 'rgba(67,160,71,0.1)',
      fill: true,
      tension: 0.3,
    }],
  };

  const dauChartData = {
    labels: allDays,
    datasets: [
      {
        label: 'Active Users',
        data: allDays.map(d => data.daily_active_users[d] || 0),
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25,118,210,0.1)',
        fill: true,
        tension: 0.3,
      },
      {
        label: 'Events',
        data: allDays.map(d => data.events_by_day[d] || 0),
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245,124,0,0.08)',
        fill: true,
        tension: 0.3,
        yAxisID: 'y1',
      },
    ],
  };

  const dauOptions = {
    ...chartOptions('Daily Active Users & Events', 'Users'),
    scales: {
      ...chartOptions('', '').scales,
      y: { beginAtZero: true, title: { display: true, text: 'Users' }, ticks: { precision: 0 }, position: 'left' },
      y1: { beginAtZero: true, title: { display: true, text: 'Events' }, ticks: { precision: 0 }, position: 'right', grid: { drawOnChartArea: false } },
    },
  };

  const methodLabels = Object.keys(data.logins_by_method);
  const methodChartData = {
    labels: methodLabels.map(m => m.charAt(0).toUpperCase() + m.slice(1)),
    datasets: [{
      data: methodLabels.map(m => data.logins_by_method[m]),
      backgroundColor: CHART_COLORS.slice(0, methodLabels.length),
      borderWidth: 0,
    }],
  };

  const featureLabels = data.top_features.map(f => f[0]);
  const featureCounts = data.top_features.map(f => f[1]);
  const featureChartData = {
    labels: featureLabels.map(l => l.replace(/_/g, ' ')),
    datasets: [{
      label: 'Usage Count',
      data: featureCounts,
      backgroundColor: CHART_COLORS.slice(0, featureLabels.length),
      borderRadius: 6,
    }],
  };

  const pageLabels = Object.keys(data.page_views);
  const pageChartData = {
    labels: pageLabels.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
    datasets: [{
      data: pageLabels.map(p => data.page_views[p]),
      backgroundColor: CHART_COLORS.slice(0, pageLabels.length),
      borderWidth: 0,
    }],
  };

  const durationPages = Object.keys(data.avg_page_durations_sec);
  const durationChartData = {
    labels: durationPages.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
    datasets: [{
      label: 'Avg Duration (sec)',
      data: durationPages.map(p => data.avg_page_durations_sec[p]),
      backgroundColor: CHART_COLORS.slice(0, durationPages.length),
      borderRadius: 6,
    }],
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary.main">Admin Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform usage overview
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Period</InputLabel>
            <Select value={days} label="Period" onChange={e => setDays(e.target.value)}>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={14}>Last 14 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={60}>Last 60 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchData} color="primary"><Refresh /></IconButton>
        </Stack>
      </Stack>

      {/* ── KPI Cards ────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Total Users" value={data.total_users} color="#43a047" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Logins" value={data.total_logins} subtitle={`last ${days} days`} color="#1976d2" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Sessions" value={data.total_sessions} subtitle={`last ${days} days`} color="#f57c00" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Events" value={data.total_events} subtitle={`last ${days} days`} color="#8e24aa" />
        </Grid>
      </Grid>

      {/* ── Charts Row 1 ─────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: isMobile ? 280 : 340 }}>
            <Line data={loginsChartData} options={chartOptions('Logins Over Time', 'Logins')} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: isMobile ? 280 : 340, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Login Methods</Typography>
            <Box sx={{ width: '80%', maxWidth: 220 }}>
              <Doughnut data={methodChartData} options={{ plugins: { legend: { position: 'bottom', labels: { boxWidth: 10 } } }, cutout: '55%' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Charts Row 2 ─────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: isMobile ? 280 : 340 }}>
            <Line data={dauChartData} options={dauOptions} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: isMobile ? 280 : 340, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Page Views</Typography>
            <Box sx={{ width: '80%', maxWidth: 220 }}>
              <Doughnut data={pageChartData} options={{ plugins: { legend: { position: 'bottom', labels: { boxWidth: 10 } } }, cutout: '55%' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Charts Row 3 ─────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: isMobile ? 280 : 340 }}>
            <Bar data={featureChartData} options={chartOptions('Top Features', 'Uses')} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: isMobile ? 280 : 340 }}>
            <Bar data={durationChartData} options={chartOptions('Avg Page Duration', 'Seconds')} />
          </Paper>
        </Grid>
      </Grid>

      {/* ── Recent Logins Table ──────────────────────────────────────── */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Recent Logins</Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>}
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.recent_logins.map((row, i) => (
                <TableRow key={i} hover>
                  <TableCell>{row.email || '—'}</TableCell>
                  <TableCell>
                    <Chip label={row.login_method || 'email'} size="small" variant="outlined"
                      color={row.login_method === 'google' ? 'error' : row.login_method === 'github' ? 'default' : 'primary'} />
                  </TableCell>
                  {!isMobile && <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.ip_address || '—'}</TableCell>}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {data.recent_logins.length === 0 && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No login events yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}
